import { useCallback, useMemo, useState } from "react";

async function panicFetch(path, options = {}) {
  const headers = options.headers === null ? {} : {
    "Content-Type": "application/json",
    "X-Requested-With": "XMLHttpRequest",
    ...(options.headers || {}),
  };
  const res = await fetch(`/api/panic/${path}`, {
    credentials: "include",
    ...options,
    headers,
  });
  const json = await res.json().catch(() => null);
  if (!res.ok || !json?.ok) throw new Error(json?.error || `HTTP ${res.status}`);
  return json;
}

export function usePanicSession(steps = []) {
  const [session, setSession] = useState(null);
  const [quickReplies, setQuickReplies] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const stepsByKey = useMemo(() => {
    const map = new Map();
    for (const row of session?.steps || []) map.set(row.step_key, row);
    return map;
  }, [session]);

  const loadHistory = useCallback(async (sessionMode = null, limit = 12, scope = "user") => {
    setHistoryLoading(true);
    try {
      const params = new URLSearchParams();
      if (sessionMode) params.set("session_mode", sessionMode);
      if (limit) params.set("limit", String(limit));
      if (scope) params.set("scope", scope);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const data = await panicFetch(`list.php${qs}`, { method: "GET", headers: null });
      setHistory(Array.isArray(data.items) ? data.items : []);
      return Array.isArray(data.items) ? data.items : [];
    } catch (err) {
      setError(err.message || "Errore caricamento storico Panic Mode");
      return [];
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const hydrate = useCallback(async (sessionMode = null, sessionId = null) => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (sessionMode) params.set("session_mode", sessionMode);
      if (sessionId) params.set("session_id", String(sessionId));
      const qs = params.toString() ? `?${params.toString()}` : "";
      const data = await panicFetch(`get.php${qs}`, { method: "GET", headers: null });
      setSession(data.session || null);
      setQuickReplies(Array.isArray(data.quick_replies) ? data.quick_replies : []);
    } catch (err) {
      setError(err.message || "Errore caricamento Panic Mode");
    } finally {
      setLoading(false);
    }
  }, []);

  const start = useCallback(async (activityType, sessionMode = "operator", reuse = false) => {
    setSaving(true);
    setError("");
    try {
      const data = await panicFetch("start.php", {
        method: "POST",
        body: JSON.stringify({ activity_type: activityType, session_mode: sessionMode, reuse }),
      });
      setSession(data.session || null);
      setQuickReplies(Array.isArray(data.quick_replies) ? data.quick_replies : []);
      return data.session;
    } catch (err) {
      setError(err.message || "Errore avvio Panic Mode");
      throw err;
    } finally {
      setSaving(false);
    }
  }, []);

  const saveStep = useCallback(async (step, status, valueText = "", valueJson = {}) => {
    if (!session?.id) return null;
    setSaving(true);
    setError("");
    try {
      const data = await panicFetch("save-step.php", {
        method: "POST",
        body: JSON.stringify({
          session_id: session.id,
          step_key: step.id,
          priority: step.priority,
          status,
          value_text: valueText,
          value_json: valueJson,
        }),
      });
      setSession(data.session || null);
      return data.session;
    } catch (err) {
      setError(err.message || "Errore salvataggio step");
      throw err;
    } finally {
      setSaving(false);
    }
  }, [session?.id]);

  const saveCorrectiveAction = useCallback(async (step, correctiveAction) => {
    if (!session?.id) return null;
    setSaving(true);
    setError("");
    try {
      const data = await panicFetch("save-corrective-action.php", {
        method: "POST",
        body: JSON.stringify({
          session_id: session.id,
          step_key: step.id,
          priority: step.priority,
          corrective_action: correctiveAction,
        }),
      });
      setSession(data.session || null);
      return data.session;
    } catch (err) {
      setError(err.message || "Errore azione correttiva");
      throw err;
    } finally {
      setSaving(false);
    }
  }, [session?.id]);

  const saveNote = useCallback(async (notes, inspectorNotes, responsibleName = "") => {
    if (!session?.id) return null;
    setSaving(true);
    setError("");
    try {
      const data = await panicFetch("save-note.php", {
        method: "POST",
        body: JSON.stringify({
          session_id: session.id,
          notes,
          inspector_notes: inspectorNotes,
          responsible_name: responsibleName,
        }),
      });
      setSession(data.session || null);
      return data.session;
    } catch (err) {
      setError(err.message || "Errore salvataggio note");
      throw err;
    } finally {
      setSaving(false);
    }
  }, [session?.id]);

  const complete = useCallback(async ({ notes, inspectorNotes, responsibleName }) => {
    if (!session?.id) return null;
    setSaving(true);
    setError("");
    try {
      const data = await panicFetch("complete.php", {
        method: "POST",
        body: JSON.stringify({
          session_id: session.id,
          notes,
          inspector_notes: inspectorNotes,
          responsible_name: responsibleName,
        }),
      });
      setSession(data.session || null);
      return data.session;
    } catch (err) {
      setError(err.message || "Errore chiusura Panic Mode");
      throw err;
    } finally {
      setSaving(false);
    }
  }, [session?.id]);

  const progress = useMemo(() => {
    const done = steps.filter((step) => {
      const row = stepsByKey.get(step.id);
      return ["OK", "NON_OK", "NA"].includes(row?.status);
    }).length;
    return { done, total: steps.length };
  }, [steps, stepsByKey]);

  const reset = useCallback(() => {
    setSession(null);
    setQuickReplies([]);
    setLoading(false);
    setSaving(false);
    setError("");
  }, []);

  return {
    session,
    quickReplies,
    history,
    loading,
    historyLoading,
    saving,
    error,
    reset,
    stepsByKey,
    progress,
    hydrate,
    loadHistory,
    start,
    saveStep,
    saveCorrectiveAction,
    saveNote,
    complete,
  };
}
